import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { toast } from 'react-toastify'
import {
  createProduct,
  deleteProduct,
  getProduct,
  listProducts,
  updateProduct,
} from '../api/products'
import type { Product, ProductInput } from '../types/product'

type ApiError = AxiosError<{ message?: string }>

function getErrorMessage(error: unknown) {
  if (error && typeof error === 'object' && 'message' in (error as ApiError)) {
    const err = error as ApiError
    return err.response?.data?.message ?? err.message ?? 'Ocurrió un error inesperado'
  }
  if (error instanceof Error) {
    return error.message
  }
  return 'Ocurrió un error inesperado'
}

export function useProducts(q?: string) {
  const normalizedQuery = q?.trim() ?? ''

  return useQuery<Product[]>({
    queryKey: ['products', { q: normalizedQuery || undefined }],
    queryFn: () =>
      listProducts(
        normalizedQuery ? { q: normalizedQuery } : undefined,
      ),
    placeholderData: keepPreviousData,
    retry: 1,
    meta: { queryName: 'products' },
  })
}

export function useProduct(id?: string | number) {
  return useQuery<Product>({
    queryKey: ['product', id],
    enabled: Boolean(id),
    queryFn: () => {
      if (!id) {
        throw new Error('Producto no encontrado')
      }
      return getProduct(id)
    },
    retry: 1,
    placeholderData: keepPreviousData,
  })
}

export function useCreateProduct() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: createProduct,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] })
      toast.success('Producto creado correctamente')
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })
}

export function useUpdateProduct() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<ProductInput> | FormData }) =>
      updateProduct(id, body),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['products'] })
      if (variables.id) {
        queryClient.invalidateQueries({ queryKey: ['product', variables.id] })
      }
      toast.success('Producto actualizado correctamente')
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })
}

export function useDeleteProduct() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: deleteProduct,
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['products'] })
      if (typeof id === 'string') {
        queryClient.invalidateQueries({ queryKey: ['product', id] })
      }
      toast.success('Producto eliminado correctamente')
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })
}
